import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import css from "../Auth.scss"
import Radio from "@components/Radio/Radio.js"
import { getAssetPath, sanitizer } from "@commonUtils"
import { directAxiosInstance } from "@lib/api"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"
import { setConflictCaseInfo, setDefault } from "../reducer"
import { setDefaultAuthConfig } from "@containers/App/reducer"
import Button from "@components/Button/Button"
import { knowMoreEmailConflictGA, selectConflictingEmailGA, addNewEmailConflictGA } from "../GAEvents"
import { useDispatch, useSelector } from "react-redux"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import Dialog from "@components/Dialog/Dialog.js"
import ARIAButton from "@wrappers/ARIAButton/ARIAButton"

function ConflictEmailList({ forwardHandler, state, localDispatch }) {
    const dispatch = useDispatch()
    const { authConfig } = useSelector((state) => state.shellReducer)
    const { info, conflictInfo } = state
    const [dialog, toggleDialog] = useState(false)

    // we are using "active" as a flag so that untill the request is completed, we do not allow multiple request
    // on multiple clicks on cta
    const [active, setActive] = useState(false)
    const [selectedRadio, setSelectedRadio] = useState("")
    const [ctaLabel, setCtaLabel] = useState("Continue")
    const [errMsg, setErrMsg] = useState(null)
    const [iconUrl, setIconUrl] = useState(null)
    useEffect(() => {
        if (conflictInfo.optionsList?.length > 0) {
            setSelectedRadio(conflictInfo.optionsList[0].value)
        }
    }, [conflictInfo.optionsList])

    /**
     * @description Function to resolve email conflict based on user selection
     */

    const handleSubmit = () => {
        if (!active) {
            setActive(true)
            setIconUrl(getAssetPath("svgs/icons/loading-wheel_24px_background.svg"))
            directAxiosInstance()
                .post("auth/phone/create_token_conflict_resolve", {
                    number: info.phone,
                    email: selectedRadio,
                })
                .then((res) => {
                    if (res && res.data) {
                        const userFlags = res?.data?.user_flags
                        if (userFlags.is_otp_required) {
                            selectConflictingEmailGA("Success")
                            localDispatch(setConflictCaseInfo({ conflictEmail: selectedRadio }))
                            forwardHandler("OtpConflict")
                        }
                    }
                    setIconUrl(null)
                    setCtaLabel("Continue")
                    setActive(false)
                })
                .catch((err) => {
                    setIconUrl(null)
                    setCtaLabel("Continue")

                    setErrMsg(err?.data?.error?.message || "Server error")
                    setActive(false)
                    selectConflictingEmailGA("Error")
                })
        }
    }

    return (
        <div className={css.rightContainer}>
            {!authConfig.restrictUserFromClosingAuth && (
                <div className={`flex ${css.crossIcon} ${css.cursorPointer}`}>
                    <div>
                        <ARIAButton
                            onClick={() => {
                                dispatch(setDefaultAuthConfig())
                                localDispatch(setDefault())
                            }}
                            ariaLabel={"Close email conflict list popup"}
                        >
                            <img src={getAssetPath("svgs/icons/cross_18px_primary.svg")} alt={"Close icon"} />
                        </ARIAButton>
                    </div>
                </div>
            )}

            <div className={css.authContainer}>
                <div className={` ${css.paddingAuth} flex flexColumn justifyBetween`}>
                    <div>
                        <div>
                            {conflictInfo.header && (
                                <span className={css.heading}>{conflictInfo.header}</span>
                            )}
                            {conflictInfo.description && (
                                <div className={`smallRegular textSecondary `}>
                                    {conflictInfo.description}{" "}
                                    {conflictInfo.cta?.text && (
                                        <ConditionalLink
                                            className={`textSecondary smallMedium marginTop-8 ${css.cursorPointer}`}
                                            onClick={() => {
                                                knowMoreEmailConflictGA()
                                                toggleDialog(true)
                                            }}
                                        >
                                            <u>{conflictInfo.cta.text}</u>
                                        </ConditionalLink>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="marginTop-24">
                            {conflictInfo.optionsList?.length > 0 &&
                                conflictInfo.optionsList.map((val, index) => {
                                    return (
                                        <div
                                            className={`${index !== 0 && "marginTop-16"} ${
                                                css.radioContainer
                                            } ${css.cursorPointer} `}
                                            key={val.value}
                                        >
                                            <Radio
                                                child={
                                                    <span
                                                        className={`${
                                                            val.value === selectedRadio
                                                                ? "bodyMedium textPrimary alignCenter"
                                                                : "smallMedium"
                                                        } ${css.cursorPointer}`}
                                                    >
                                                        {val.display}
                                                    </span>
                                                }
                                                isSelected={val.value === selectedRadio}
                                                onClick={() => setSelectedRadio(val.value)}
                                                isDisabled={false}
                                            />
                                        </div>
                                    )
                                })}

                            <div className="marginTop-16">
                                <ConditionalLink
                                    className={`textAction bodyMedium marginBoth-8 ${css.cursorPointer}`}
                                    onClick={() => {
                                        addNewEmailConflictGA()
                                        forwardHandler("EmailConflict")
                                    }}
                                >
                                    <div className="flex">
                                        <ImageLoader
                                            src={getAssetPath("svgs/icons/plus_18px_action.svg")}
                                            height="18px"
                                            width="18px"
                                        />
                                        <div className={`${css.marginLeft10} bodySemiBold textAction`}>
                                            {"Enter new email id"}
                                        </div>
                                    </div>
                                </ConditionalLink>
                            </div>
                        </div>
                    </div>

                    <div className="flexColumn alignCenter  ">
                        {errMsg && <span className={`marginBoth-4 smallRegular errorColor`}>{errMsg}</span>}
                        <Button
                            label={!iconUrl ? ctaLabel : null}
                            iconUrl={iconUrl}
                            isDisabled={iconUrl ? true : false}
                            onClick={() => handleSubmit()}
                        />
                        <div className="marginTop-16 flexColumn alignCenter ">
                            <div className={`${css.footerSubText} flexColumn alignCenter `}>
                                <div className="textTertiary">
                                    By signing up, you agree to our
                                    <div>
                                        <span
                                            className={`textTertiary ${css.underlineText}`}
                                            onClick={() => {
                                                dispatch(setDefaultAuthConfig())
                                                window.location.href = "/tnc"
                                            }}
                                        >
                                            Terms and Conditions
                                        </span>
                                        <span className={css.marginBoth4}>&</span>
                                        <span
                                            className={`textTertiary ${css.underlineText}`}
                                            onClick={() => {
                                                dispatch(setDefaultAuthConfig())
                                                window.location.href = "/PrivacyPolicy"
                                            }}
                                        >
                                            Privacy policy
                                        </span>
                                    </div>
                                </div>
                                <ConditionalLink
                                    className={` ${css.marginFooter} smallSemiBold textAction ${css.cursorPointer}`}
                                    onClick={() => forwardHandler("NeedHelp")}
                                >
                                    <span>Need Help?</span>
                                    <span className={css.marginLeft4}> Get in touch</span>
                                </ConditionalLink>
                            </div>
                        </div>
                    </div>
                    <Dialog isOpen={dialog === true}>
                        <div className={` ${css.conflictPopupContainer}`}>
                            <div className=" flexColumn alignCenter ">
                                <div className={`flex`}>
                                    {conflictInfo.cta?.image_url && (
                                        <ImageLoader
                                            src={conflictInfo.cta?.image_url}
                                            className={`marginTop-8 flex justifyCenter alignCenter`}
                                            height="46px"
                                            width={"46px"}
                                        />
                                    )}
                                </div>

                                {conflictInfo.cta?.header && (
                                    <div className={`l4SemiBold marginTop-16`}>
                                        {conflictInfo.cta?.header}
                                    </div>
                                )}
                                {conflictInfo.cta?.description && (
                                    <div className={`bodyRegular marginTop-16 textSecondary `}>
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: sanitizer(conflictInfo.cta?.description),
                                            }}
                                        />
                                    </div>
                                )}
                                <div className="flexColumn alignCenter">
                                    {conflictInfo.cta?.confirm_cta && (
                                        <ConditionalLink>
                                            <div className={`col-4 flex justifyCenter marginTop-16 `}>
                                                <Button
                                                    label={conflictInfo.cta?.confirm_cta}
                                                    onClick={() => toggleDialog(false)}
                                                />
                                            </div>
                                        </ConditionalLink>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    )
}

ConflictEmailList.propTypes = {
    forwardHandler: PropTypes.func.isRequired,

    state: PropTypes.shape({
        info: PropTypes.shape({
            phone: PropTypes.string,
        }),
        conflictInfo: PropTypes.shape({
            optionsList: PropTypes.array,
            header: PropTypes.string,
            description: PropTypes.string,
            cta: PropTypes.shape({
                text: PropTypes.string,
                image_url: PropTypes.string,
                header: PropTypes.string,
                description: PropTypes.string,
                confirm_cta: PropTypes.string,
            }),
        }),
    }).isRequired,
    localDispatch: PropTypes.func.isRequired,
}

export default ConflictEmailList
